'use client'

import { useEffect, useMemo, useState } from 'react'
import type { Race } from 'src/types/domain/Race'
import { latestRevealAtMs } from './pastRounds'

/**
 * The ANTI-SPOILER CLOCK (B19 Task 2, kept by B19b).
 *
 * `race_settled` lands on the EVENT clock, which leads the viewer's frames by
 * their playout buffer, so every Birdie surface withholds a round until its own
 * reveal hold has elapsed — exactly as the capsule withholds the final score.
 * That needs ONE re-render, at that instant, and no other: a 1 Hz tick would
 * re-run the whole history scan sixty times a minute for a value that changes
 * once a round.
 *
 * `latestRevealAtMs` is deliberately independent of "now", which is what makes
 * the timeout armable: a value derived from the current clock would change on
 * every render and re-arm the timer forever.
 *
 * B19b moved it out of the deleted `useBirdieHistory` (whose deep read is now
 * the shared surfaces' own — `PastRacesSidebar` and `RoundStatsPopover` already
 * take it) into its own module, because the three Birdie branches that need it
 * live in three different components now. A caller on a non-Birdie game hands
 * over an EMPTY list: `latestRevealAtMs` then returns undefined, the effect
 * returns immediately and no timer is ever armed.
 */
export function useBirdieRevealClock(races: readonly Race[]): number {
  const [nowMs, setNowMs] = useState<number>(() => Date.now())
  const revealAtMs = useMemo(() => latestRevealAtMs(races), [races])

  useEffect(() => {
    if (revealAtMs === undefined) return
    const delay = revealAtMs - Date.now()
    if (delay <= 0) {
      setNowMs((previous) => (previous >= revealAtMs ? previous : Date.now()))
      return
    }
    const timer = setTimeout(() => {
      setNowMs(Date.now())
    }, delay)
    return () => {
      clearTimeout(timer)
    }
  }, [revealAtMs])

  return nowMs
}
